import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, Plus, Edit3, Trash2, Loader2, X, CheckCircle2, XCircle,
  Phone, Bell, AlertTriangle, RefreshCw,
} from "lucide-react";
import { api } from "../lib/api";

const ROLE_LABELS = {
  pastor: { label: "Pastor", color: "bg-amber-50 text-amber-700 border-amber-200" },
  intercessor: { label: "Intercessor(a)", color: "bg-violet-50 text-violet-700 border-violet-200" },
  lider: { label: "Líder", color: "bg-sky-50 text-sky-700 border-sky-200" },
  auxiliar: { label: "Auxiliar", color: "bg-slate-50 text-slate-700 border-slate-200" },
};

const DEFAULT_FORM = {
  name: "", phone: "", role: "intercessor",
  receives_prayers: true, receives_crisis: false,
  active: true, notes: "",
};

function fmtPhone(p) {
  if (!p) return ", ";
  const m = p.match(/^\+(\d{2})(\d{2})(\d{1})?(\d{4})(\d{4})$/);
  if (m) return `+${m[1]} (${m[2]}) ${m[3] || ""}${m[3] ? " " : ""}${m[4]}-${m[5]}`;
  return p;
}

function fmtRelative(iso) {
  if (!iso) return "Nunca";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export default function AdminPastoralTeam() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/admin/pastoral-team");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar equipe pastoral.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (editing === "new") {
        await api.post("/admin/pastoral-team", form);
        toast.success("Membro adicionado à equipe pastoral.");
      } else {
        await api.put(`/admin/pastoral-team/${editing.id}`, form);
        toast.success("Membro atualizado.");
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remover "${m.name}" da equipe pastoral? Ele não receberá mais alertas de pedidos de oração.`)) return;
    try {
      await api.delete(`/admin/pastoral-team/${m.id}`);
      toast.success("Membro removido."); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover.");
    }
  };

  return (
    <section data-testid="admin-pastoral-team-tab" className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-600 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Intercessão
          </p>
          <h2 className="font-display text-3xl text-navy mt-1">Equipe Pastoral</h2>
          <p className="text-[13px] text-navy/55 mt-1.5 max-w-xl">
            Membros que recebem WhatsApp imediato a cada novo pedido de oração e/ou alerta de crise.
            Cada cadastro precisa ter feito <strong>join no Sandbox</strong> Twilio para receber.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="team-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-navy/55 hover:text-navy"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="team-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white hover:bg-amber-600 text-[11px] uppercase tracking-widest transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar membro
          </button>
        </div>
      </header>

      <div className="text-[12px] text-navy/55" data-testid="team-summary">
        {loading ? "Carregando…" : (
          <span>
            <strong className="text-navy">{items.length}</strong> membro{items.length === 1 ? "" : "s"} ·{" "}
            <strong className="text-emerald-700">{items.filter(i => i.active && i.receives_prayers).length}</strong> ativos para alertas de oração
          </span>
        )}
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="text-center text-navy/55 py-12">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Carregando…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-navy/55 py-12">
            <Users className="w-8 h-8 mx-auto mb-2 text-navy/30" />
            <p>Nenhum membro cadastrado.</p>
            <p className="text-xs mt-1">Adicione pelo menos o pastor para que pedidos de oração sejam encaminhados via WhatsApp.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="team-list">
            {items.map((m) => {
              const meta = ROLE_LABELS[m.role] || ROLE_LABELS.intercessor;
              return (
                <li key={m.id} data-testid={`team-row-${m.id}`} className="p-4 flex items-center gap-4 flex-wrap hover:bg-[hsl(var(--parchment))]/50">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-navy truncate">{m.name}</p>
                      <span className={`inline-block text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                      {!m.active && <span className="inline-block text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 bg-rose-50 text-rose-700 border-rose-200">inativo</span>}
                    </div>
                    <p className="text-[12px] text-navy/55 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{fmtPhone(m.phone)}</span>
                      {m.receives_prayers && <span className="inline-flex items-center gap-1 text-amber-700"><Bell className="w-3 h-3" />Orações</span>}
                      {m.receives_crisis && <span className="inline-flex items-center gap-1 text-rose-700"><AlertTriangle className="w-3 h-3" />Crises</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-navy/50">Último alerta</p>
                    <p className="text-[11px] text-navy/65">{fmtRelative(m.last_alert_at)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEditing(m)} data-testid={`team-edit-${m.id}`} title="Editar"
                      className="p-2 rounded-full hover:bg-navy/5 text-navy/70">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(m)} data-testid={`team-delete-${m.id}`} title="Remover"
                      className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editing && (
        <FormModal
          member={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </section>
  );
}

function FormModal({ member, onClose, onSave }) {
  const [form, setForm] = useState(() => member ? {
    name: member.name || "",
    phone: member.phone || "",
    role: member.role || "intercessor",
    receives_prayers: member.receives_prayers !== false,
    receives_crisis: !!member.receives_crisis,
    active: member.active !== false,
    notes: member.notes || "",
  } : { ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);

  const valid = form.name.trim().length >= 2 && form.phone.replace(/[^\d+]/g, "").length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    const payload = { ...form };
    if (!payload.notes) delete payload.notes;
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div data-testid="team-form-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-navy/60 backdrop-blur-sm">
      <div onClick={onClose} className="absolute inset-0" />
      <form onSubmit={submit} className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 m-0 sm:m-4 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-600">{member ? "Editar" : "Novo"}</p>
            <h3 className="font-display text-2xl text-navy">Membro da equipe pastoral</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-navy/5">
            <X className="w-4 h-4 text-navy/60" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Nome *">
            <input required minLength={2} maxLength={80}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="team-form-name"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-amber-500/60 rounded-lg outline-none text-sm" />
          </Field>
          <Field label="WhatsApp * (formato +55…)">
            <input required value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="team-form-phone"
              placeholder="+5581999998888 ou 81999998888"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-amber-500/60 rounded-lg outline-none text-sm font-mono" />
          </Field>
          <Field label="Função">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="team-form-role"
              className="w-full mt-1 h-11 px-3 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-amber-500/60 rounded-lg outline-none text-sm">
              <option value="pastor">Pastor</option>
              <option value="lider">Líder</option>
              <option value="intercessor">Intercessor(a)</option>
              <option value="auxiliar">Auxiliar</option>
            </select>
          </Field>
          <Field label="Notas (opcional)">
            <textarea rows={2} maxLength={300}
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="team-form-notes"
              className="w-full mt-1 px-3 py-2 bg-[hsl(var(--parchment))] border border-transparent focus:bg-white focus:border-amber-500/60 rounded-lg outline-none text-sm" />
          </Field>
          <div className="space-y-2 pt-2 border-t border-border">
            <Toggle label="Ativo" checked={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
              testId="team-form-active" />
            <Toggle label="Recebe alertas de pedidos de oração" checked={form.receives_prayers}
              onChange={(v) => setForm({ ...form, receives_prayers: v })}
              testId="team-form-prayers" />
            <Toggle label="Recebe alertas de crise (urgente)" checked={form.receives_crisis}
              onChange={(v) => setForm({ ...form, receives_crisis: v })}
              testId="team-form-crisis" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest text-navy/60 hover:text-navy">Cancelar</button>
          <button type="submit" disabled={!valid || saving} data-testid="team-form-save"
            className="px-5 py-2 rounded-full bg-navy text-white hover:bg-amber-600 text-[11px] uppercase tracking-widest disabled:opacity-50">
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-navy/55">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 w-full">
      <span className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors ${checked ? "bg-emerald-600" : "bg-navy/20"}`}>
        <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
      <span className="text-[12px] text-navy/75">{label}</span>
    </button>
  );
}
